import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import BASE_URL from '../../url';
import '../styles/erprecruitment.css';
import {
  Flame,
  Search,
  Users,
  Briefcase,
  Download,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  User,
  X,
  Loader,
  FileText,
  FileSpreadsheet,
  Check,
  AlertTriangle
} from 'lucide-react';
import { generateBenchHeatReportHtml, triggerHtmlDownload } from '../utils/benchHeatReportUtils';
import CandidateDetail from '../../Resume_Submission/Components/CandidateDetail';

const SESSION_KEY = 'bench_heat_report_filters_v1';

// ── Searchable Client Select Dropdown ──────────────────────────────
const SearchableBenchClientSelect = ({ clients = [], selectedClient = '', onSelect, isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = (clients || []).filter(c => {
    if (!c) return false;
    const cl = c.toLowerCase();
    if (c.includes('(IN)') || cl.includes('(india)') || cl.includes('offshore')) return false;
    return cl.includes(search.toLowerCase());
  });

  return (
    <div ref={containerRef} style={{ position: "relative", minWidth: "170px" }}>
      <label style={{ display: "block", fontSize: "12px", color: isDarkMode ? "#8a93a3" : "#475569", fontWeight: "500", marginBottom: "6px" }}>
        Client
      </label>
      <div
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 12px",
          background: isDarkMode ? "#161b22" : "white",
          border: isDarkMode ? "1px solid #262d38" : "1px solid #cbd5e1",
          borderRadius: "6px", cursor: "pointer", fontSize: "14px",
          color: isDarkMode ? "#e6e9ef" : "#334155",
          height: "38px", boxSizing: "border-box"
        }}
      >
        <span style={{ fontWeight: selectedClient ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: selectedClient ? "#3b5bfd" : "#334155", display: "flex", alignItems: "center", gap: "6px" }}>
          {selectedClient ? (
            <>
              <Briefcase size={13} color="#3b5bfd" /> {selectedClient}
            </>
          ) : (
            <>
              <Briefcase size={13} color="#64748b" /> All Clients
            </>
          )}
        </span>
        <ChevronDown size={13} color="#64748b" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", marginLeft: "6px", flexShrink: 0 }} />
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, marginTop: "4px",
            width: "240px",
            background: isDarkMode ? "#161b22" : "white",
            border: isDarkMode ? "1px solid #262d38" : "1px solid #cbd5e1",
            borderRadius: "8px", boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
            zIndex: 9999, overflow: "hidden"
          }}
        >
          <div style={{ padding: "6px 8px", borderBottom: isDarkMode ? "1px solid #262d38" : "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: "6px", background: isDarkMode ? "#1c2229" : "#f8fafc" }}>
            <Search size={13} color="#94a3b8" />
            <input
              type="text"
              placeholder="Search client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                border: "none", outline: "none", fontSize: "12px", width: "100%",
                background: "transparent", color: isDarkMode ? "#e6e9ef" : "#334155"
              }}
              autoFocus
            />
            {search && (
              <X size={12} color="#94a3b8" style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setSearch(""); }} />
            )}
          </div>

          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            <div
              onClick={() => { onSelect(""); setIsOpen(false); setSearch(""); }}
              style={{
                padding: "8px 12px", fontSize: "13px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: selectedClient === "" ? (isDarkMode ? "rgba(59,91,253,0.15)" : "#eff6ff") : "transparent",
                color: selectedClient === "" ? "#3b5bfd" : (isDarkMode ? "#e6e9ef" : "#334155"),
                fontWeight: selectedClient === "" ? 600 : 400
              }}
              onMouseEnter={(e) => { if (selectedClient !== "") e.currentTarget.style.background = isDarkMode ? "#1c2229" : "#f8fafc"; }}
              onMouseLeave={(e) => { if (selectedClient !== "") e.currentTarget.style.background = "transparent"; }}
            >
              <span>All Clients {clients.length > 0 ? `(${clients.length})` : ''}</span>
              {selectedClient === "" && <Check size={13} color="#3b5bfd" />}
            </div>

            {filtered.map((cName, idx) => {
              const isSelected = selectedClient.toLowerCase() === cName.toLowerCase();
              return (
                <div
                  key={idx}
                  onClick={() => { onSelect(cName); setIsOpen(false); setSearch(""); }}
                  style={{
                    padding: "8px 12px", fontSize: "13px", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: isSelected ? (isDarkMode ? "rgba(59,91,253,0.15)" : "#eff6ff") : "transparent",
                    color: isSelected ? "#3b5bfd" : (isDarkMode ? "#e6e9ef" : "#334155"),
                    fontWeight: isSelected ? 600 : 400
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = isDarkMode ? "#1c2229" : "#f8fafc"; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cName}</span>
                  {isSelected && <Check size={13} color="#3b5bfd" />}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div style={{ padding: "10px 12px", fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>
                {clients.length === 0 ? "No active clients in selected period" : "No matching clients"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Searchable Recruiter Select Dropdown ──────────────────────────────
const SearchableBenchRecruiterSelect = ({ recruiters, selectedRecruiter, onSelect, isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredRecruiters = (recruiters || []).filter(r =>
    (r.name || r.username || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={containerRef} style={{ position: "relative", minWidth: "180px", maxWidth: "240px" }}>
      <label style={{ display: "block", fontSize: "12px", color: "#475569", fontWeight: "500", marginBottom: "6px" }}>
        Bench Recruiter
      </label>
      <div
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 12px",
          background: isDarkMode ? "#161b22" : "white",
          border: isDarkMode ? "1px solid #262d38" : "1px solid #cbd5e1",
          borderRadius: "6px", cursor: "pointer", fontSize: "14px",
          color: isDarkMode ? "#e6e9ef" : "#334155",
          height: "38px", boxSizing: "border-box"
        }}
      >
        <span style={{ fontWeight: selectedRecruiter ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: selectedRecruiter ? "#3b5bfd" : "#334155", display: "flex", alignItems: "center", gap: "6px" }}>
          {selectedRecruiter ? (
            <>
              <User size={13} color="#3b5bfd" /> {selectedRecruiter}
            </>
          ) : (
            <>
              <Users size={13} color="#64748b" /> All Bench Recruiters
            </>
          )}
        </span>
        <ChevronDown size={13} color="#64748b" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", marginLeft: "6px", flexShrink: 0 }} />
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, marginTop: "4px",
            width: "260px",
            background: isDarkMode ? "#161b22" : "white",
            border: isDarkMode ? "1px solid #262d38" : "1px solid #cbd5e1",
            borderRadius: "8px", boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
            zIndex: 9999, overflow: "hidden"
          }}
        >
          <div style={{ padding: "6px 8px", borderBottom: isDarkMode ? "1px solid #262d38" : "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: "6px", background: isDarkMode ? "#1c2229" : "#f8fafc" }}>
            <Search size={13} color="#94a3b8" />
            <input
              type="text"
              autoFocus
              placeholder="Search recruiter..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", border: "none", outline: "none", background: "transparent",
                fontSize: "12px", color: isDarkMode ? "#e6e9ef" : "#1e293b"
              }}
            />
            {search && (
              <X size={12} color="#94a3b8" style={{ cursor: "pointer" }} onClick={() => setSearch("")} />
            )}
          </div>

          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            <div
              onClick={() => { onSelect(""); setIsOpen(false); setSearch(""); }}
              style={{
                padding: "7px 12px", fontSize: "12px", cursor: "pointer",
                background: !selectedRecruiter ? (isDarkMode ? "#1e3a4a" : "#eff6ff") : "transparent",
                color: !selectedRecruiter ? "#3b5bfd" : (isDarkMode ? "#e6e9ef" : "#334155"),
                fontWeight: !selectedRecruiter ? 700 : 500,
                borderBottom: isDarkMode ? "1px solid #262d38" : "1px solid #f1f5f9",
                display: "flex", alignItems: "center", gap: "6px"
              }}
            >
              <Users size={13} color="#3b5bfd" />
              <span>All Bench Recruiters ({recruiters.length})</span>
            </div>
            {filteredRecruiters.length === 0 ? (
              <div style={{ padding: "10px", fontSize: "11px", color: "#94a3b8", textAlign: "center" }}>No recruiters found</div>
            ) : (
              filteredRecruiters.map((r, idx) => {
                const name = r.name || r.username;
                const isSelected = selectedRecruiter === name;
                return (
                  <div
                    key={idx}
                    onClick={() => { onSelect(name); setIsOpen(false); setSearch(""); }}
                    style={{
                      padding: "7px 12px", fontSize: "12px", cursor: "pointer",
                      background: isSelected ? (isDarkMode ? "#1e3a4a" : "#e0f2fe") : "transparent",
                      color: isSelected ? "#38bdf8" : (isDarkMode ? "#e6e9ef" : "#334155"),
                      fontWeight: isSelected ? 700 : 400,
                      display: "flex", alignItems: "center", justifyContent: "space-between"
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <User size={12} color="#64748b" /> {name}
                    </span>
                    {isSelected && <Check size={13} color="#38bdf8" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const toUtcYMD = (d) => {
  if (!d) return "";
  const dateObj = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(dateObj.getTime())) return "";
  const year = dateObj.getUTCFullYear();
  const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BenchHeatReport = () => {
  const navigate = useNavigate();
  const getStoredFilters = () => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.warn("Could not read sessionStorage:", e);
    }
    return {
      quickDate: 'All Time',
      startDate: '',
      endDate: '',
      selectedRecruiter: '',
      selectedClient: '',
      isDarkMode: false
    };
  };

  const initialFilters = getStoredFilters();

  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [recruitersList, setRecruitersList] = useState([]);
  const [error, setError] = useState('');

  // Filters State
  const [quickDate, setQuickDate] = useState(initialFilters.quickDate || 'All Time');
  const [startDate, setStartDate] = useState(initialFilters.startDate ?? '');
  const [endDate, setEndDate] = useState(initialFilters.endDate ?? '');
  const [selectedRecruiter, setSelectedRecruiter] = useState(initialFilters.selectedRecruiter || '');
  const [selectedClient, setSelectedClient] = useState(initialFilters.selectedClient || '');
  const [expandedRecruiter, setExpandedRecruiter] = useState(null);
  const [submissionsSearch, setSubmissionsSearch] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const isDarkMode = false;

  // Download Dropdown State
  const [isReportDropdownOpen, setIsReportDropdownOpen] = useState(false);
  const reportDropdownRef = useRef(null);

  // Bench Aging Table Pagination State with sessionStorage
  const [benchAgingPage, setBenchAgingPage] = useState(() => {
    const saved = sessionStorage.getItem('bench_heat_aging_page');
    return saved !== null ? parseInt(saved, 10) : 1;
  });
  const [benchAgingPageSize, setBenchAgingPageSize] = useState(() => {
    const saved = sessionStorage.getItem('bench_heat_aging_pageSize');
    return saved !== null ? parseInt(saved, 10) : 30;
  });

  const handleBenchAgingPageChange = (p) => {
    setBenchAgingPage(p);
    sessionStorage.setItem('bench_heat_aging_page', p);
  };

  const handleBenchAgingPageSizeChange = (size) => {
    setBenchAgingPageSize(size);
    setBenchAgingPage(1);
    sessionStorage.setItem('bench_heat_aging_pageSize', size);
    sessionStorage.setItem('bench_heat_aging_page', 1);
  };

  // Save to Session Storage & Local Storage
  useEffect(() => {
    try {
      const payload = JSON.stringify({
        quickDate,
        startDate,
        endDate,
        selectedRecruiter,
        selectedClient,
        isDarkMode
      });
      sessionStorage.setItem(SESSION_KEY, payload);
      localStorage.setItem(SESSION_KEY, payload);
    } catch (e) {
      console.warn("Could not save to sessionStorage:", e);
    }
  }, [quickDate, startDate, endDate, selectedRecruiter, selectedClient, isDarkMode]);

  // Click outside download menu
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (reportDropdownRef.current && !reportDropdownRef.current.contains(e.target)) {
        setIsReportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // 1. Fetch Recruiters List for Dropdown
  useEffect(() => {
    const fetchRecruiters = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/recruitment/roles/bench-recruiters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data && res.data.success) {
          setRecruitersList(res.data.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch bench recruiters", err);
      }
    };
    fetchRecruiters();
  }, []);

  // 2. Fetch Bench Heat Report Data
  const fetchReportData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      let url = `${BASE_URL}/api/recruitment/roles/bench-heat-report?`;
      const params = new URLSearchParams();

      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (selectedRecruiter) params.append('recruiter', selectedRecruiter);
      if (selectedClient) params.append('client', selectedClient);

      const res = await axios.get(`${url}${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data && res.data.success) {
        setReportData(res.data.data);
      } else {
        setError('Unable to load bench heat report.');
      }
    } catch (err) {
      console.error("Failed to fetch bench heat report:", err);
      setError('Failed to fetch bench heat report data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedRecruiter, selectedClient]);

  // Quick Date Change Handler (UTC standard)
  const handleQuickDateChange = (val) => {
    setQuickDate(val);
    if (val === 'All Time') {
      setStartDate('');
      setEndDate('');
      return;
    }

    const now = new Date();
    const utcYear = now.getUTCFullYear();
    const utcMonth = now.getUTCMonth();
    const utcDate = now.getUTCDate();
    const utcDay = now.getUTCDay(); // 0 = Sunday

    let start = new Date(Date.UTC(utcYear, utcMonth, utcDate));
    let end = new Date(Date.UTC(utcYear, utcMonth, utcDate));

    if (val === 'Today') {
      start = new Date(Date.UTC(utcYear, utcMonth, utcDate));
      end = new Date(Date.UTC(utcYear, utcMonth, utcDate));
    } else if (val === 'Yesterday') {
      start = new Date(Date.UTC(utcYear, utcMonth, utcDate - 1));
      end = new Date(Date.UTC(utcYear, utcMonth, utcDate - 1));
    } else if (val === 'This Week') {
      const diff = utcDate - utcDay + (utcDay === 0 ? -6 : 1); 
      start = new Date(Date.UTC(utcYear, utcMonth, diff));
      end = new Date(Date.UTC(utcYear, utcMonth, utcDate));
    } else if (val === 'Last Week') {
      const diff = utcDate - utcDay + (utcDay === 0 ? -6 : 1) - 7;
      start = new Date(Date.UTC(utcYear, utcMonth, diff));
      end = new Date(Date.UTC(utcYear, utcMonth, diff + 6));
    } else if (val === 'This Month') {
      start = new Date(Date.UTC(utcYear, utcMonth, 1));
      end = new Date(Date.UTC(utcYear, utcMonth, utcDate));
    } else if (val === 'Last Month') {
      start = new Date(Date.UTC(utcYear, utcMonth - 1, 1));
      end = new Date(Date.UTC(utcYear, utcMonth, 0));
    } else if (val === 'This Quarter') {
      const quarter = Math.floor(utcMonth / 3);
      start = new Date(Date.UTC(utcYear, quarter * 3, 1));
      end = new Date(Date.UTC(utcYear, utcMonth, utcDate));
    } else if (val === 'Last Quarter') {
      const quarter = Math.floor(utcMonth / 3) - 1;
      start = new Date(Date.UTC(utcYear, quarter * 3, 1));
      end = new Date(Date.UTC(utcYear, (quarter + 1) * 3, 0));
    } else if (val === 'Custom Range') {
      return;
    }

    setStartDate(toUtcYMD(start));
    setEndDate(toUtcYMD(end));
    setBenchAgingPage(1);
  };

  const handleResetFilters = () => {
    setQuickDate('All Time');
    setStartDate('');
    setEndDate('');
    setSelectedRecruiter('');
    setSelectedClient('');
    setBenchAgingPage(1);
  };

  // 3. Export to Excel
  const exportToExcel = () => {
    if (!reportData) return;
    try {
      const wb = XLSX.utils.book_new();

      // Sheet 1: Bench Aging
      const agingData = (reportData.benchAging || []).map(c => ({
        'Consultant': c.name,
        'Email': c.email || '',
        'Phone': c.phone || '',
        'Recruiter': c.owner,
        'Days on Bench': c.daysOnBench,
        'Last Submission': c.lastSubmission && c.lastSubmission !== '—' ? c.lastSubmission : '—',
        'Last Submission (Days Ago)': c.lastSubmissionRelative || (c.daysSinceLastSubmission !== undefined && c.daysSinceLastSubmission !== null ? `${c.daysSinceLastSubmission}d ago` : '—'),
        'Total Submissions': c.submissionCount ?? c.lifetimeSubmissions ?? 0,
        'Status': c.status,
        'Escalate Flag': c.escalate ? 'YES' : 'NO'
      }));
      const wsAging = XLSX.utils.json_to_sheet(agingData);
      XLSX.utils.book_append_sheet(wb, wsAging, "Bench Aging");

      // Sheet 2: Recruiter Performance
      const scoreData = (reportData.recruiterScorecard || []).map(r => ({
        'Rank': r.rank,
        'Recruiter': r.recruiter,
        'Submissions': r.submissions,
        'Interviews': r.interviews,
        'Placements': r.placements
      }));
      const wsScore = XLSX.utils.json_to_sheet(scoreData);
      XLSX.utils.book_append_sheet(wb, wsScore, "Recruiter Performance");

      // Sheet 3: Candidate Submissions
      if (reportData.recruiterSubmissions && reportData.recruiterSubmissions.length > 0) {
        const subsData = reportData.recruiterSubmissions.map(s => ({
          'Candidate': s.candidateName,
          'Email': s.candidateEmail || '',
          'Phone': s.candidatePhone || '',
          'Job Code': s.jobCode || '',
          'Job Title': s.jobTitle || '',
          'Client': s.clientName || '',
          'Applied Date': s.appliedDate ? new Date(s.appliedDate).toISOString().slice(0, 10) : '',
          'Current Stage': s.currentStage || '',
          'Status': s.status || '',
          'Recruiter': s.recruiterName || ''
        }));
        const wsSubs = XLSX.utils.json_to_sheet(subsData);
        XLSX.utils.book_append_sheet(wb, wsSubs, "Candidate Submissions");
      }

      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Bench_Heat_Report_${dateStr}.xlsx`);
    } catch (e) {
      console.error("Failed to export Excel:", e);
    }
  };

  // 4. Export to Standalone Executive HTML / PDF
  const exportToExecutiveHtml = () => {
    let filterParts = [];
    if (selectedRecruiter) filterParts.push(`Recruiter: ${selectedRecruiter}`);
    if (selectedClient) filterParts.push(`Client: ${selectedClient}`);
    filterParts.push(startDate && endDate ? `${startDate} to ${endDate}` : (quickDate || 'All Time'));
    const filterInfo = filterParts.join(' | ');

    const html = generateBenchHeatReportHtml({
      asOfDate: reportData?.asOfDate,
      kpiSummary: reportData?.kpiSummary || {},
      benchAging: reportData?.benchAging || [],
      recruiterScorecard: reportData?.recruiterScorecard || [],
      filterInfo
    });

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
    } else {
      triggerHtmlDownload(html, `Bench_Heat_Executive_Report_${new Date().toISOString().slice(0, 10)}.html`);
    }
  };

  const kpis = reportData?.kpiSummary || {
    totalActiveConsultants: 0,
    consultants45Plus: 0,
    avgDaysOnBench: 0,
    submissionsCount: 0,
    placementsCount: 0
  };

  const benchAging = reportData?.benchAging || [];
  const recruiterScorecard = reportData?.recruiterScorecard || [];

  const totalBenchAgingPages = Math.max(1, Math.ceil(benchAging.length / benchAgingPageSize));
  const paginatedBenchAging = useMemo(() => {
    const start = (benchAgingPage - 1) * benchAgingPageSize;
    return benchAging.slice(start, start + benchAgingPageSize);
  }, [benchAging, benchAgingPage, benchAgingPageSize]);

  // Theme Colors
  const theme = {
    bg: isDarkMode ? "#0e1116" : "#f8fafc",
    panel: isDarkMode ? "#161b22" : "#ffffff",
    panel2: isDarkMode ? "#1c2229" : "#f1f5f9",
    line: isDarkMode ? "#262d38" : "#e2e8f0",
    text: isDarkMode ? "#e6e9ef" : "#0f172a",
    muted: isDarkMode ? "#8a93a3" : "#64748b",
    mono: "'JetBrains Mono', 'SF Mono', Consolas, monospace",
    sans: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    cool: "#3f9ea8",
    warm: "#d0a03c",
    hot: "#d9622b",
    burn: "#c0362c",
    hover: isDarkMode ? "#1a2029" : "#f8fafc"
  };

  return (
    <div style={{
      backgroundColor: theme.bg,
      color: theme.text,
      fontFamily: theme.sans,
      minHeight: "100vh",
      padding: "24px 32px 60px",
      transition: "background 0.3s, color 0.3s"
    }}>
      {/* Header Bar */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        borderBottom: `1px solid ${theme.line}`,
        paddingBottom: "18px",
        marginBottom: "22px"
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h1 style={{ fontSize: "24px", fontWeight: 800, letterSpacing: "-0.01em", margin: 0, display: "flex", alignItems: "center", gap: "8px", color: theme.text }}>
              <Flame size={26} color="#d9622b" />
              Bench Heat Report
            </h1>
            <span style={{
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".06em",
              padding: "3px 8px",
              borderRadius: "12px",
              background: "rgba(217, 98, 43, 0.15)",
              color: "#d9622b"
            }}>
              Delivery &amp; Bench Sales
            </span>
          </div>
          <div style={{ color: theme.muted, fontSize: "13px", marginTop: "4px" }}>
            Real-time Consultant Aging, Recruiter Performance Scorecard &amp; SLA Tracking
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div style={{ fontFamily: theme.mono, fontSize: "12px", color: theme.muted, textAlign: "right" }}>
            <strong style={{ color: theme.text, display: "block", fontSize: "13px" }}>
              {reportData?.asOfDate || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
            </strong>
            Live Sync Active
          </div>
        </div>
      </div>

      {/* Filters & Actions Control Bar */}
      <div style={{
        background: theme.panel,
        border: `1px solid ${theme.line}`,
        borderRadius: "8px",
        padding: "16px 20px",
        marginBottom: "24px",
        display: "flex",
        flexWrap: "wrap",
        gap: "16px",
        alignItems: "flex-end",
        justifyContent: "space-between"
      }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "16px", flexWrap: "wrap" }}>
          {/* Date Filters Section */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "13px", color: "#64748b", fontWeight: "500" }}>Date filters:</span>
            <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap" }}>
              {/* Quick Select */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "12px", color: "#475569", fontWeight: "500" }}>Quick Select</label>
                <select
                  value={quickDate}
                  onChange={(e) => handleQuickDateChange(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: isDarkMode ? "1px solid #262d38" : "1px solid #cbd5e1",
                    background: isDarkMode ? "#161b22" : "white",
                    outline: "none",
                    fontSize: "14px",
                    minWidth: "140px",
                    color: theme.text,
                    height: "38px",
                    boxSizing: "border-box",
                    cursor: "pointer"
                  }}
                >
                  <option value="All Time">All Time</option>
                  <option value="Today">Today</option>
                  <option value="Yesterday">Yesterday</option>
                  <option value="This Week">This Week</option>
                  <option value="Last Week">Last Week</option>
                  <option value="This Month">This Month</option>
                  <option value="Last Month">Last Month</option>
                  <option value="This Quarter">This Quarter</option>
                  <option value="Last Quarter">Last Quarter</option>
                  <option value="Custom Range">Custom Range</option>
                </select>
              </div>

              {/* Start Date */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "12px", color: "#475569", fontWeight: "500" }}>Start date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setQuickDate('Custom Range');
                  }}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: isDarkMode ? "1px solid #262d38" : "1px solid #cbd5e1",
                    background: isDarkMode ? "#161b22" : "white",
                    outline: "none",
                    fontSize: "14px",
                    color: theme.text,
                    height: "38px",
                    boxSizing: "border-box",
                    cursor: "pointer"
                  }}
                />
              </div>

              {/* End Date */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "12px", color: "#475569", fontWeight: "500" }}>End date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setQuickDate('Custom Range');
                  }}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: isDarkMode ? "1px solid #262d38" : "1px solid #cbd5e1",
                    background: isDarkMode ? "#161b22" : "white",
                    outline: "none",
                    fontSize: "14px",
                    color: theme.text,
                    height: "38px",
                    boxSizing: "border-box",
                    cursor: "pointer"
                  }}
                />
              </div>
            </div>
          </div>

          {/* Bench Recruiter Select */}
          <SearchableBenchRecruiterSelect
            recruiters={recruitersList}
            selectedRecruiter={selectedRecruiter}
            onSelect={(name) => setSelectedRecruiter(name)}
            isDarkMode={isDarkMode}
          />

          {/* Bench Client Select */}
          <SearchableBenchClientSelect
            clients={reportData?.clientsList || []}
            selectedClient={selectedClient}
            onSelect={(name) => setSelectedClient(name)}
            isDarkMode={isDarkMode}
          />

          {/* Reset Filters */}
          {(quickDate !== 'All Time' || selectedRecruiter !== '' || selectedClient !== '' || startDate !== '' || endDate !== '') && (
            <button
              onClick={handleResetFilters}
              style={{
                background: "#fff5f5",
                border: "1px solid #e74c3c",
                color: "#e74c3c",
                padding: "8px 14px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
                height: "38px",
                boxSizing: "border-box",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px"
              }}
            >
              <RotateCcw size={13} /> Reset Filters
            </button>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexShrink: 0 }}>
          {/* Refresh Button */}
          <button
            onClick={fetchReportData}
            disabled={isLoading}
            style={{
              background: theme.panel2,
              border: `1px solid ${theme.line}`,
              color: theme.text,
              padding: "6px 12px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 600,
              height: "34px",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <RotateCcw size={13} className={isLoading ? "animate-spin" : ""} />
            Refresh
          </button>

          {/* Download Dropdown */}
          <div style={{ position: "relative" }} ref={reportDropdownRef}>
            <button
              onClick={() => setIsReportDropdownOpen(!isReportDropdownOpen)}
              style={{
                background: "#3b5bfd",
                color: "white",
                border: "none",
                padding: "6px 14px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 700,
                height: "34px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                boxShadow: "0 2px 4px rgba(59,91,253,0.2)"
              }}
            >
              <Download size={14} /> Download Report <ChevronDown size={13} />
            </button>

            {isReportDropdownOpen && (
              <div style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: "4px",
                width: "260px",
                background: theme.panel,
                border: `1px solid ${theme.line}`,
                borderRadius: "8px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
                zIndex: 9999,
                overflow: "hidden"
              }}>
                <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: theme.muted, textTransform: 'uppercase', letterSpacing: '0.5px', background: theme.panel2, borderBottom: `1px solid ${theme.line}` }}>
                  Select Report Format
                </div>

                <div
                  onClick={() => { exportToExecutiveHtml(); setIsReportDropdownOpen(false); }}
                  style={{
                    padding: "10px 14px",
                    fontSize: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    borderBottom: `1px solid ${theme.line}`,
                    color: theme.text,
                    transition: "background 0.15s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.panel2}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <span style={{ fontSize: "14px" }}>⚙️</span>
                  <div>
                    <div style={{ fontWeight: 700 }}>Selected Date Range</div>
                    <div style={{ fontSize: "10px", color: theme.muted }}>
                      {startDate && endDate ? `${startDate} — ${endDate}` : (quickDate || "All Time")}
                    </div>
                  </div>
                </div>

                <div
                  onClick={() => { exportToExecutiveHtml(); setIsReportDropdownOpen(false); }}
                  style={{
                    padding: "10px 14px",
                    fontSize: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    borderBottom: `1px solid ${theme.line}`,
                    color: theme.text,
                    transition: "background 0.15s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.panel2}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <span style={{ fontSize: "14px" }}>📑</span>
                  <div>
                    <div style={{ fontWeight: 700 }}>Standard Job Report</div>
                    <div style={{ fontSize: "10px", color: theme.muted }}>Complete consultant aging &amp; SLA (PDF/Print)</div>
                  </div>
                </div>

                <div
                  onClick={() => { exportToExcel(); setIsReportDropdownOpen(false); }}
                  style={{
                    padding: "10px 14px",
                    fontSize: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: theme.text,
                    transition: "background 0.15s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.panel2}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <span style={{ fontSize: "14px" }}>📊</span>
                  <div>
                    <div style={{ fontWeight: 700 }}>Excel Spreadsheet (.xlsx)</div>
                    <div style={{ fontSize: "10px", color: theme.muted }}>Multi-tab structured data</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div style={{ padding: "80px 20px", textAlign: "center", color: theme.muted }}>
          <Loader size={32} className="animate-spin" style={{ margin: "0 auto 12px", color: "#3b5bfd" }} />
          <div style={{ fontSize: "14px", fontWeight: 600 }}>Loading Bench Heat Report &amp; Recruiter Performance...</div>
        </div>
      ) : error ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#ef4444", background: "rgba(239,68,68,0.08)", borderRadius: "8px" }}>
          <AlertTriangle size={28} style={{ margin: "0 auto 8px" }} />
          <div>{error}</div>
          <button onClick={fetchReportData} style={{ marginTop: "12px", padding: "6px 14px", background: "#ef4444", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}>Retry</button>
        </div>
      ) : (
        <>
          {/* Top KPI Strip (4 Focused Bench KPI Cards) */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1px",
            background: theme.line,
            border: `1px solid ${theme.line}`,
            borderRadius: "8px",
            overflow: "hidden",
            marginBottom: "32px"
          }}>
            <div style={{ background: theme.panel, padding: "18px 20px" }}>
              <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: ".06em", color: theme.muted, fontWeight: 600 }}>
                Active Bench Candidates
              </div>
              <div style={{ fontFamily: theme.mono, fontSize: "26px", fontWeight: 800, marginTop: "6px", color: "#3b5bfd" }}>
                {kpis.totalActiveConsultants ?? benchAging.length}
              </div>
            </div>

            <div style={{ background: theme.panel, padding: "18px 20px" }}>
              <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: ".06em", color: theme.muted, fontWeight: 600 }}>
                Consultants 45+ Days
              </div>
              <div style={{ fontFamily: theme.mono, fontSize: "26px", fontWeight: 800, marginTop: "6px", color: "#ff6d5f" }}>
                {kpis.consultants45Plus}
              </div>
            </div>

            <div style={{ background: theme.panel, padding: "18px 20px" }}>
              <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: ".06em", color: theme.muted, fontWeight: 600 }}>
                Submissions
              </div>
              <div style={{ fontFamily: theme.mono, fontSize: "26px", fontWeight: 800, marginTop: "6px", color: theme.text }}>
                {kpis.submissionsCount}
              </div>
            </div>

            <div style={{ background: theme.panel, padding: "18px 20px" }}>
              <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: ".06em", color: theme.muted, fontWeight: 600 }}>
                Placements
              </div>
              <div style={{ fontFamily: theme.mono, fontSize: "26px", fontWeight: 800, marginTop: "6px", color: "#5fd478" }}>
                {kpis.placementsCount}
              </div>
            </div>
          </div>

          {/* Section 1: Bench Aging — By Consultant */}
          <div style={{ marginBottom: "36px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
              <h2 style={{ fontSize: "14px", margin: 0, textTransform: "uppercase", letterSpacing: ".05em", color: theme.muted, fontWeight: 700 }}>
                Bench Aging — By Consultant
              </h2>
              <span style={{
                fontFamily: theme.mono,
                fontSize: "12px",
                color: theme.muted,
                background: theme.panel2,
                padding: "2px 8px",
                borderRadius: "4px"
              }}>
                {quickDate === 'Today' ? `${benchAging.length} Submitted Today` :
                 quickDate === 'This Month' ? `${benchAging.length} Submitted This Month` :
                 quickDate === 'All Time' || (!startDate && !endDate) ? `${benchAging.length} Total on Bench` :
                 `${benchAging.length} Submitted in Period`}
              </span>
            </div>

            <div style={{
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: "520px",
              background: theme.panel,
              borderRadius: "8px",
              border: `1px solid ${theme.line}`,
              position: "relative"
            }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 10, background: theme.panel2 }}>
                  <tr style={{ background: theme.panel2 }}>
                    <th style={{ position: "sticky", top: 0, background: theme.panel2, zIndex: 10, textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Consultant</th>
                    <th style={{ position: "sticky", top: 0, background: theme.panel2, zIndex: 10, textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Email</th>
                    <th style={{ position: "sticky", top: 0, background: theme.panel2, zIndex: 10, textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Phone</th>
                    <th style={{ position: "sticky", top: 0, background: theme.panel2, zIndex: 10, textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Recruiter</th>
                    <th style={{ position: "sticky", top: 0, background: theme.panel2, zIndex: 10, textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Days on Bench</th>
                    <th style={{ position: "sticky", top: 0, background: theme.panel2, zIndex: 10, textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Last Submission</th>
                    <th style={{ position: "sticky", top: 0, background: theme.panel2, zIndex: 10, textAlign: "center", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Total Submissions</th>
                    <th style={{ position: "sticky", top: 0, background: theme.panel2, zIndex: 10, textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBenchAging.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: "32px", textAlign: "center", color: theme.muted, fontSize: "13px" }}>
                        No bench candidate submissions recorded for the selected date range.
                      </td>
                    </tr>
                  ) : (
                    paginatedBenchAging.map((c, idx) => {
                      const isCritical = c.status === 'CRITICAL' || c.daysOnBench >= 46;
                      const pillBg = c.status === 'CRITICAL' ? 'rgba(192, 54, 44, .18)' : ((c.status === 'HIGH RISK' || c.status === 'HOT') ? 'rgba(217, 98, 43, .15)' : ((c.status === 'MODERATE' || c.status === 'WARMING') ? 'rgba(208, 160, 60, .15)' : 'rgba(63, 158, 80, .15)'));
                      const pillColor = c.status === 'CRITICAL' ? '#ff6d5f' : ((c.status === 'HIGH RISK' || c.status === 'HOT') ? '#d9622b' : ((c.status === 'MODERATE' || c.status === 'WARMING') ? '#d0a03c' : '#5fd478'));

                      return (
                        <tr
                          key={idx}
                          style={{
                            background: isCritical ? (isDarkMode ? "rgba(192, 54, 44, .07)" : "#fef2f2") : "transparent",
                            borderBottom: `1px solid ${theme.line}`,
                            transition: "background 0.15s"
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = isCritical ? (isDarkMode ? "rgba(192, 54, 44, .12)" : "#fee2e2") : theme.hover}
                          onMouseLeave={(e) => e.currentTarget.style.background = isCritical ? (isDarkMode ? "rgba(192, 54, 44, .07)" : "#fef2f2") : "transparent"}
                        >
                          <td style={{ padding: "11px 14px", fontSize: "13px", fontWeight: 700 }}>
                            {c.candidateId ? (
                              <span
                                onClick={() => setSelectedCandidateId(c.candidateId)}
                                style={{
                                  color: "#3b5bfd",
                                  textDecoration: "none",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  fontWeight: 700
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"}
                                onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}
                                title={`Click to view ${c.name}'s overview profile`}
                              >
                                {c.name}
                              </span>
                            ) : (
                              <span style={{ color: theme.text }}>{c.name}</span>
                            )}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: "12.5px", fontFamily: theme.mono, color: theme.text }}>
                            {c.email || '—'}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: "12.5px", fontFamily: theme.mono, color: theme.muted }}>
                            {c.phone || '—'}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: "13px", color: theme.text }}>
                            {c.owner}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: "13px", fontFamily: theme.mono, fontWeight: 700, color: theme.text }}>
                            {c.daysOnBench}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: "12.5px", fontFamily: theme.mono, color: c.lastSubmission && c.lastSubmission !== '—' && c.lastSubmission !== 'No Submissions' ? theme.text : theme.muted }}>
                            {c.lastSubmission && c.lastSubmission !== '—' && c.lastSubmission !== 'No Submissions' ? (
                              <div>
                                <div style={{ fontWeight: 600 }}>{c.lastSubmissionDateFormatted || c.lastSubmission}</div>
                                <div style={{
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  color: c.daysSinceLastSubmission === 0 ? "#16a34a" : (c.daysSinceLastSubmission <= 7 ? "#3b5bfd" : (c.daysSinceLastSubmission <= 21 ? "#d97706" : "#dc2626")),
                                  marginTop: "2px"
                                }}>
                                  {c.lastSubmissionRelative || (
                                    c.daysSinceLastSubmission !== undefined && c.daysSinceLastSubmission !== null
                                      ? (c.daysSinceLastSubmission === 0 ? 'Today' : c.daysSinceLastSubmission === 1 ? '1 day ago' : `${c.daysSinceLastSubmission} days ago`)
                                      : ''
                                  )}
                                </div>
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: "13px", textAlign: "center" }}>
                            <span style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: "28px",
                              padding: "3px 8px",
                              borderRadius: "12px",
                              fontFamily: theme.mono,
                              fontSize: "12px",
                              fontWeight: 700,
                              background: (c.submissionCount || c.lifetimeSubmissions) > 0 ? "rgba(59, 91, 253, 0.12)" : "rgba(100, 116, 139, 0.12)",
                              color: (c.submissionCount || c.lifetimeSubmissions) > 0 ? "#3b5bfd" : theme.muted
                            }}>
                              {c.submissionCount !== undefined && c.submissionCount !== null ? c.submissionCount : (c.lifetimeSubmissions || 0)}
                            </span>
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: "13px" }}>
                            <span style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "3px 9px",
                              borderRadius: "20px",
                              fontFamily: theme.mono,
                              fontSize: "11px",
                              fontWeight: 700,
                              background: pillBg,
                              color: pillColor
                            }}>
                              {c.status}
                            </span>
                            {c.escalate && (
                              <span style={{
                                fontFamily: theme.mono,
                                fontSize: "10px",
                                background: "#c0362c",
                                color: "#fff",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                marginLeft: "8px",
                                letterSpacing: ".03em",
                                fontWeight: 700
                              }}>
                                ESCALATE
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer matching screenshot */}
            <div style={{
              padding: "12px 18px",
              borderTop: `1px solid ${theme.line}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "13px",
              color: theme.muted,
              background: theme.panel,
              borderRadius: "0 0 8px 8px",
              border: `1px solid ${theme.line}`,
              borderTop: "none",
              flexWrap: "wrap",
              gap: "12px"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span>
                  Showing <strong style={{ color: theme.text }}>{benchAging.length > 0 ? (benchAgingPage - 1) * benchAgingPageSize + 1 : 0}–{Math.min(benchAgingPage * benchAgingPageSize, benchAging.length)}</strong> of <strong style={{ color: theme.text }}>{benchAging.length}</strong>
                </span>
                <select
                  value={benchAgingPageSize}
                  onChange={(e) => handleBenchAgingPageSizeChange(parseInt(e.target.value, 10))}
                  style={{
                    padding: "3px 8px",
                    borderRadius: "6px",
                    border: `1px solid ${theme.line}`,
                    fontSize: "12px",
                    color: theme.text,
                    background: theme.panel2,
                    outline: "none",
                    cursor: "pointer"
                  }}
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <button
                  disabled={benchAgingPage <= 1}
                  onClick={() => handleBenchAgingPageChange(benchAgingPage - 1)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "5px 12px",
                    borderRadius: "6px",
                    border: `1px solid ${theme.line}`,
                    background: theme.panel,
                    color: benchAgingPage <= 1 ? (isDarkMode ? "#4b5563" : "#cbd5e1") : theme.muted,
                    cursor: benchAgingPage <= 1 ? "not-allowed" : "pointer",
                    fontSize: "12px",
                    fontWeight: 500
                  }}
                >
                  <ChevronLeft size={14} /> Prev
                </button>

                {Array.from({ length: totalBenchAgingPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalBenchAgingPages || Math.abs(p - benchAgingPage) <= 2)
                  .map((p, idx, arr) => {
                    const prev = arr[idx - 1];
                    return (
                      <React.Fragment key={p}>
                        {prev && p - prev > 1 && (
                          <span style={{ padding: "0 4px", color: theme.muted }}>...</span>
                        )}
                        <button
                          onClick={() => handleBenchAgingPageChange(p)}
                          style={{
                            minWidth: "28px",
                            height: "28px",
                            padding: "0 6px",
                            borderRadius: "6px",
                            border: p === benchAgingPage ? "none" : `1px solid ${theme.line}`,
                            background: p === benchAgingPage ? "#3b5bfd" : theme.panel,
                            color: p === benchAgingPage ? "#fff" : theme.muted,
                            fontWeight: p === benchAgingPage ? 700 : 500,
                            fontSize: "12px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    );
                  })}

                <button
                  disabled={benchAgingPage >= totalBenchAgingPages}
                  onClick={() => handleBenchAgingPageChange(benchAgingPage + 1)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "5px 12px",
                    borderRadius: "6px",
                    border: `1px solid ${theme.line}`,
                    background: theme.panel,
                    color: benchAgingPage >= totalBenchAgingPages ? (isDarkMode ? "#4b5563" : "#cbd5e1") : theme.muted,
                    cursor: benchAgingPage >= totalBenchAgingPages ? "not-allowed" : "pointer",
                    fontSize: "12px",
                    fontWeight: 500
                  }}
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>

          {/* Section 2: Recruiter Performance — Bench Sales (Expandable Inline Accordion) */}
          <div style={{ marginBottom: "36px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <h2 style={{ fontSize: "14px", margin: 0, textTransform: "uppercase", letterSpacing: ".05em", color: theme.muted, fontWeight: 700 }}>
                  Recruiter Performance — Bench Sales
                </h2>
                <span style={{
                  fontFamily: theme.mono,
                  fontSize: "12px",
                  color: "#3b5bfd",
                  background: "rgba(59, 91, 253, 0.1)",
                  padding: "2px 8px",
                  borderRadius: "4px",
                  fontWeight: 600
                }}>
                  Click any recruiter to view candidate submissions
                </span>
              </div>
            </div>

            <div style={{ overflowX: "auto", background: theme.panel, borderRadius: "8px", border: `1px solid ${theme.line}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: theme.panel2 }}>
                    <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Rank</th>
                    <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Recruiter</th>
                    <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Submissions</th>
                    <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Interviews</th>
                    <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "11px 14px", borderBottom: `1px solid ${theme.line}` }}>Placements</th>
                  </tr>
                </thead>
                <tbody>
                  {recruiterScorecard.map((r, idx) => {
                    const isExpanded = expandedRecruiter && expandedRecruiter.toLowerCase() === r.recruiter.toLowerCase();
                    const rankBg = r.rank === 1 ? 'rgba(63,158,80,.2)' : (r.rank === 2 ? 'rgba(63,158,80,.12)' : theme.panel2);
                    const rankColor = r.rank === 1 || r.rank === 2 ? '#5fd478' : theme.muted;

                    // Match candidate submissions for this recruiter from loaded dataset
                    const rName = (r.recruiter || '').toLowerCase().trim();
                    const allSubs = reportData?.recruiterSubmissions || [];
                    const recruiterSubs = allSubs.filter(s => {
                      const subUserId = s.SubmittedByUserId ? String(s.SubmittedByUserId) : null;
                      const subRec = (s.recruiterName || '').toLowerCase().trim();
                      const owner = (s.OwnerRecruiterUserName || '').toLowerCase().trim();
                      const submitter = (s.SubmittedByUserName || '').toLowerCase().trim();

                      if (r.userId && subUserId === String(r.userId)) return true;
                      if (rName && (subRec === rName || owner === rName || submitter === rName)) return true;
                      if (r.username && (subRec === r.username.toLowerCase() || owner === r.username.toLowerCase() || submitter === r.username.toLowerCase())) return true;
                      if (r.email && (subRec === r.email.toLowerCase() || owner === r.email.toLowerCase() || submitter === r.email.toLowerCase())) return true;
                      if (rName && (subRec.includes(rName) || owner.includes(rName) || submitter.includes(rName))) return true;
                      return false;
                    });

                    const searchLower = (submissionsSearch || '').toLowerCase().trim();
                    const displayedSubs = searchLower
                      ? recruiterSubs.filter(s => 
                          (s.candidateName || '').toLowerCase().includes(searchLower) ||
                          (s.jobCode || '').toLowerCase().includes(searchLower) ||
                          (s.jobTitle || '').toLowerCase().includes(searchLower) ||
                          (s.clientName || '').toLowerCase().includes(searchLower) ||
                          (s.candidateEmail || '').toLowerCase().includes(searchLower) ||
                          (s.candidatePhone || '').toLowerCase().includes(searchLower)
                        )
                      : recruiterSubs;

                    return (
                      <React.Fragment key={idx}>
                        <tr
                          onClick={() => {
                            setExpandedRecruiter(prev => prev && prev.toLowerCase() === r.recruiter.toLowerCase() ? null : r.recruiter);
                            setSubmissionsSearch('');
                          }}
                          style={{
                            background: isExpanded ? (isDarkMode ? "rgba(59, 91, 253, 0.15)" : "#eff6ff") : "transparent",
                            borderBottom: isExpanded ? "none" : `1px solid ${theme.line}`,
                            cursor: "pointer",
                            transition: "background 0.15s"
                          }}
                          onMouseEnter={(e) => {
                            if (!isExpanded) e.currentTarget.style.background = theme.hover;
                          }}
                          onMouseLeave={(e) => {
                            if (!isExpanded) e.currentTarget.style.background = "transparent";
                          }}
                          title={`Click to ${isExpanded ? 'collapse' : 'expand'} candidate submissions for ${r.recruiter}`}
                        >
                          <td style={{ padding: "12px 14px", fontSize: "13px" }}>
                            <span style={{
                              width: "26px",
                              height: "26px",
                              borderRadius: "6px",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontFamily: theme.mono,
                              fontWeight: 800,
                              fontSize: "12px",
                              background: rankBg,
                              color: rankColor
                            }}>
                              {r.rank}
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: "13px", fontWeight: 700, color: isExpanded ? "#3b5bfd" : theme.text }}>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "22px",
                                height: "22px",
                                borderRadius: "4px",
                                background: isExpanded ? "#3b5bfd" : theme.panel2,
                                color: isExpanded ? "#ffffff" : theme.muted,
                                transition: "all 0.2s"
                              }}>
                                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </span>
                              <span>{r.recruiter}</span>
                              <span style={{
                                fontSize: "11px",
                                fontFamily: theme.mono,
                                fontWeight: 600,
                                color: isExpanded ? "#3b5bfd" : theme.muted,
                                background: isExpanded ? "rgba(59, 91, 253, 0.12)" : theme.panel2,
                                padding: "1px 6px",
                                borderRadius: "10px"
                              }}>
                                {r.submissions} {r.submissions === 1 ? 'sub' : 'subs'}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: "13px", fontFamily: theme.mono, fontWeight: 700, color: theme.text }}>
                            {r.submissions}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: "13px", fontFamily: theme.mono, color: theme.text }}>
                            {r.interviews}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: "13px", fontFamily: theme.mono, fontWeight: 700, color: "#5fd478" }}>
                            {r.placements}
                          </td>
                        </tr>

                        {/* Inline Nested Accordion Submissions View */}
                        {isExpanded && (
                          <tr style={{ background: isDarkMode ? "rgba(59, 91, 253, 0.06)" : "#f8fafc", borderBottom: `1px solid ${theme.line}` }}>
                            <td colSpan={5} style={{ padding: "12px 18px 20px" }}>
                              <div style={{
                                background: theme.panel,
                                border: `1px solid ${theme.line}`,
                                borderRadius: "8px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                                overflow: "hidden"
                              }}>
                                {/* Accordion Sub-Header */}
                                <div style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "10px 16px",
                                  borderBottom: `1px solid ${theme.line}`,
                                  background: theme.panel2,
                                  flexWrap: "wrap",
                                  gap: "10px"
                                }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <User size={15} color="#3b5bfd" />
                                    <span style={{ fontSize: "13px", fontWeight: 700, color: "#3b5bfd" }}>
                                      Candidate Submissions by {r.recruiter} ({recruiterSubs.length} profiles)
                                    </span>
                                  </div>

                                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <div style={{ position: "relative" }}>
                                      <Search size={12} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: theme.muted }} />
                                      <input
                                        type="text"
                                        placeholder="Search candidate, job, client..."
                                        value={submissionsSearch}
                                        onChange={(e) => setSubmissionsSearch(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          background: theme.panel,
                                          border: `1px solid ${theme.line}`,
                                          borderRadius: "6px",
                                          padding: "4px 8px 4px 28px",
                                          fontSize: "12px",
                                          color: theme.text,
                                          outline: "none",
                                          width: "220px"
                                        }}
                                      />
                                      {submissionsSearch && (
                                        <X
                                          size={12}
                                          style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: theme.muted }}
                                          onClick={(e) => { e.stopPropagation(); setSubmissionsSearch(''); }}
                                        />
                                      )}
                                    </div>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedRecruiter(null);
                                      }}
                                      style={{
                                        background: theme.panel,
                                        border: `1px solid ${theme.line}`,
                                        color: theme.muted,
                                        padding: "4px 10px",
                                        borderRadius: "6px",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        cursor: "pointer",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "4px"
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.color = theme.text; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.color = theme.muted; }}
                                    >
                                      <X size={12} /> Collapse
                                    </button>
                                  </div>
                                </div>

                                {/* Accordion Sub-Table */}
                                <div style={{ overflowX: "auto", maxHeight: "400px", overflowY: "auto" }}>
                                  <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                                    <thead style={{ position: "sticky", top: 0, zIndex: 5, background: theme.panel }}>
                                      <tr style={{ background: theme.panel }}>
                                        <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "10px 14px", borderBottom: `1px solid ${theme.line}` }}>Candidate</th>
                                        <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "10px 14px", borderBottom: `1px solid ${theme.line}` }}>Contact</th>
                                        <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "10px 14px", borderBottom: `1px solid ${theme.line}` }}>Job ID / Code</th>
                                        <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "10px 14px", borderBottom: `1px solid ${theme.line}` }}>Job Title</th>
                                        <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "10px 14px", borderBottom: `1px solid ${theme.line}` }}>Client</th>
                                        <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "10px 14px", borderBottom: `1px solid ${theme.line}` }}>Applied Date</th>
                                        <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "10px 14px", borderBottom: `1px solid ${theme.line}` }}>Current Stage</th>
                                        <th style={{ textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", color: theme.muted, fontWeight: 700, padding: "10px 14px", borderBottom: `1px solid ${theme.line}` }}>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {displayedSubs.length === 0 ? (
                                        <tr>
                                          <td colSpan={8} style={{ padding: "24px", textAlign: "center", color: theme.muted, fontSize: "12.5px" }}>
                                            {searchLower ? `No candidate submissions matching "${submissionsSearch}".` : `No candidate submissions found for ${r.recruiter} in this period.`}
                                          </td>
                                        </tr>
                                      ) : (
                                        displayedSubs.map((s, sIdx) => {
                                          const stageLabel = s.currentStage ? s.currentStage.replace(/_/g, ' ') : 'Submitted to Client';
                                          const formattedDate = s.appliedDate ? new Date(s.appliedDate).toISOString().slice(0, 10) : '—';

                                          return (
                                            <tr
                                              key={sIdx}
                                              style={{
                                                borderBottom: `1px solid ${theme.line}`,
                                                transition: "background 0.15s"
                                              }}
                                              onMouseEnter={(e) => { e.currentTarget.style.background = theme.hover; }}
                                              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                            >
                                              <td style={{ padding: "10px 14px", fontSize: "12.5px", fontWeight: 700 }}>
                                                {s.candidateId ? (
                                                  <span
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSelectedCandidateId(s.candidateId);
                                                    }}
                                                    style={{
                                                      color: "#3b5bfd",
                                                      textDecoration: "none",
                                                      cursor: "pointer",
                                                      fontWeight: 700
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                                                    title={`Click to view ${s.candidateName}'s overview profile`}
                                                  >
                                                    {s.candidateName}
                                                  </span>
                                                ) : (
                                                  <span style={{ color: theme.text }}>{s.candidateName}</span>
                                                )}
                                              </td>
                                              <td style={{ padding: "10px 14px", fontSize: "11.5px", fontFamily: theme.mono }}>
                                                <div style={{ color: theme.text }}>{s.candidateEmail || '—'}</div>
                                                <div style={{ color: theme.muted, fontSize: "10.5px" }}>{s.candidatePhone || '—'}</div>
                                              </td>
                                              <td style={{ padding: "10px 14px", fontSize: "12px", fontFamily: theme.mono, fontWeight: 700, color: theme.text }}>
                                                {s.jobCode ? (
                                                  <span
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const targetId = s.roleId || s.jobId || s.jobCode;
                                                      if (targetId) navigate(`/role-detail/${targetId}`);
                                                    }}
                                                    style={{ cursor: "pointer", color: "#3b5bfd" }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                                                    title={`Click to view Role Overview for ${s.jobCode}`}
                                                  >
                                                    {s.jobCode}
                                                  </span>
                                                ) : (
                                                  '—'
                                                )}
                                              </td>
                                              <td style={{ padding: "10px 14px", fontSize: "12.5px", color: theme.text, fontWeight: 500 }}>
                                                {s.jobTitle ? (
                                                  <span
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const targetId = s.roleId || s.jobId || s.jobCode;
                                                      if (targetId) navigate(`/role-detail/${targetId}`);
                                                    }}
                                                    style={{ cursor: "pointer", color: "#3b5bfd" }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                                                    title={`Click to open Role Overview: ${s.jobTitle}`}
                                                  >
                                                    {s.jobTitle}
                                                  </span>
                                                ) : (
                                                  '—'
                                                )}
                                              </td>
                                              <td style={{ padding: "10px 14px", fontSize: "12px", color: theme.muted }}>
                                                {s.clientName || '—'}
                                              </td>
                                              <td style={{ padding: "10px 14px", fontSize: "11.5px", fontFamily: theme.mono, color: theme.text }}>
                                                {formattedDate}
                                              </td>
                                              <td style={{ padding: "10px 14px", fontSize: "11.5px" }}>
                                                <span style={{
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  padding: "2px 8px",
                                                  borderRadius: "16px",
                                                  fontFamily: theme.mono,
                                                  fontSize: "10.5px",
                                                  fontWeight: 700,
                                                  background: "rgba(139, 92, 246, 0.12)",
                                                  color: "#8b5cf6",
                                                  textTransform: "capitalize"
                                                }}>
                                                  {stageLabel.toLowerCase()}
                                                </span>
                                              </td>
                                              <td style={{ padding: "10px 14px", fontSize: "11.5px" }}>
                                                <span style={{
                                                  fontFamily: theme.mono,
                                                  fontSize: "10.5px",
                                                  fontWeight: 700,
                                                  color: theme.muted
                                                }}>
                                                  {s.status || 'ACTIVE'}
                                                </span>
                                              </td>
                                            </tr>
                                          );
                                        })
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer Legend */}
          <div style={{
            marginTop: "40px",
            fontSize: "11px",
            color: theme.muted,
            borderTop: `1px solid ${theme.line}`,
            paddingTop: "16px",
            lineHeight: 1.6
          }}>
            <strong>Calculation Rules:</strong> Status tiers based on activity &amp; aging: Active (last sub &le; 7d) · Moderate (8-21d) · High Risk (22-35d) · Critical (36+d or idle &gt; 45d).<br />
            Click any recruiter row to inspect their candidate submissions and job performance.
          </div>
        </>
      )}

      {/* Candidate Quick Overview Modal Popup */}
      {selectedCandidateId && (
        <CandidateDetail
          candidateId={selectedCandidateId}
          isModal={true}
          onClose={() => setSelectedCandidateId(null)}
          initialTab="overview"
        />
      )}
    </div>
  );
};

export default BenchHeatReport;
